import React from 'react';

import type { RJSFSchema } from '@rjsf/utils';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';

import PropertyForm from '~/components/Common/Forms/PropertyForm';

import { CREATE_NEW_PROPERTY, PROPERTY_FORM_ID } from './constants';
import type { FlashOfferProperty } from './types';

interface Props {
  activeProperties: Array<FlashOfferProperty>;
  hasPropertyFormError: boolean;
  isBusy: boolean;
  isCreatingProperty: boolean;
  isLoadingPropertyForm: boolean;
  onDraftChange: (formData: Record<string, unknown>) => void;
  onPropertyCreateError: () => void;
  onPropertyCreateStart: () => void;
  onPropertyCreateSuccess: (property: FlashOfferProperty) => void;
  onPropertySelect: (propertyId: string) => void;
  onRetryPropertySchema: () => void;
  propertyFormData: Record<string, unknown>;
  propertySchema: RJSFSchema | undefined;
  selectedPropertyId: string;
  vendorId: string;
}

export default function PropertyStep({
  activeProperties,
  hasPropertyFormError,
  isBusy,
  isCreatingProperty,
  isLoadingPropertyForm,
  onDraftChange,
  onPropertyCreateError,
  onPropertyCreateStart,
  onPropertyCreateSuccess,
  onPropertySelect,
  onRetryPropertySchema,
  propertyFormData,
  propertySchema,
  selectedPropertyId,
  vendorId,
}: Props) {
  return (
    <Box sx={{ minHeight: isLoadingPropertyForm ? 560 : undefined }}>
      {activeProperties.length > 0 && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="flash-offer-property-select-label">Property</InputLabel>
            <Select
              label="Property"
              labelId="flash-offer-property-select-label"
              value={selectedPropertyId}
              disabled={isBusy}
              onChange={(event) => onPropertySelect(event.target.value)}
            >
              <MenuItem value={CREATE_NEW_PROPERTY}>Create new property</MenuItem>
              {activeProperties.map((property) => (
                <MenuItem key={property.id} value={property.id}>
                  {property.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider />
        </Stack>
      )}

      {isLoadingPropertyForm && (
        <Alert icon={<CircularProgress size={16} />} severity="info">
          Loading property details.
        </Alert>
      )}
      {!isLoadingPropertyForm && hasPropertyFormError && (
        <Alert
          severity="error"
          action={
            <Button disabled={isBusy} onClick={onRetryPropertySchema}>
              Try again
            </Button>
          }
        >
          Unable to load property details.
        </Alert>
      )}
      {!isCreatingProperty && !isLoadingPropertyForm && !hasPropertyFormError && (
        <Alert severity="info">Selected existing property will be used for this Flash offer.</Alert>
      )}
      {isCreatingProperty && !isLoadingPropertyForm && !hasPropertyFormError && propertySchema && (
        <PropertyForm
          key="new-property"
          disabled={isBusy}
          formId={PROPERTY_FORM_ID}
          hideActions
          autoPopulateCoordinates
          geocodeOnAddressChange={false}
          onDraftChange={onDraftChange}
          onSubmitError={onPropertyCreateError}
          onSubmitStart={onPropertyCreateStart}
          onSubmitSuccess={onPropertyCreateSuccess}
          property={propertyFormData}
          schema={propertySchema}
          vendorId={vendorId}
        />
      )}
    </Box>
  );
}
